import type { GameEvent, ITransport } from '../interfaces/index.js'

export type ConnectionState = 'disconnected' | 'connecting' | 'queuing' | 'matched' | 'error'

export interface WebSocketTransportOptions {
  serverUrl: string
  playerId: string
  playerName: string
  /** MMR / rank points used for skill-based matchmaking. Defaults to 1000 if omitted. */
  playerMmr?: number
  /** Match format to request from the server. Defaults to '1v1'. */
  matchFormat?: '1v1' | '2v2' | '3v3' | 'coop'
  onMatchFound?: (matchId: string, oppId: string, oppName: string, youAre: 'player1' | 'player2', opponentMmr?: number) => void
  onOpponentDisconnected?: () => void
  onConnectionError?: (msg: string) => void
  onQueued?: (pos: number) => void
  /** Coop mode: called when server relays partner's current HP. */
  onCoopPartnerHp?: (hp: number) => void
  /** Coop mode: called when partner's base has been destroyed. */
  onCoopPartnerDefeated?: () => void
}

export class WebSocketTransport implements ITransport {
  private ws: WebSocket | null = null
  private receiveCallback: ((e: GameEvent) => void) | null = null
  private matchId: string | null = null
  private _localPlayerId: 'player1' | 'player2' | null = null
  private state: ConnectionState = 'disconnected'
  private readonly opts: WebSocketTransportOptions

  constructor(opts: WebSocketTransportOptions) {
    this.opts = opts
  }

  async connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.state = 'connecting'
      const timer = setTimeout(() => {
        this.state = 'error'
        reject(new Error('Timeout'))
      }, 10000)
      try {
        this.ws = new WebSocket(this.opts.serverUrl)
      } catch (e) {
        clearTimeout(timer)
        reject(new Error(String(e)))
        return
      }
      this.ws.onopen = () => {
        clearTimeout(timer)
        this.state = 'queuing'
        const format = this.opts.matchFormat ?? '1v1'
        if (format === 'coop') {
          this.ws!.send(
            JSON.stringify({
              type: 'JOIN_COOP_QUEUE',
              playerId: this.opts.playerId,
              playerName: this.opts.playerName,
            })
          )
        } else {
          this.ws!.send(
            JSON.stringify({
              type: 'JOIN_QUEUE',
              playerId: this.opts.playerId,
              playerName: this.opts.playerName,
              mmr: this.opts.playerMmr ?? 1000,
              matchFormat: format,
            })
          )
        }
        resolve()
      }
      this.ws.onmessage = (ev: MessageEvent<string>) => this.handleMessage(ev.data)
      this.ws.onerror = () => {
        clearTimeout(timer)
        this.state = 'error'
        this.opts.onConnectionError?.('Connection failed')
        reject(new Error('WS error'))
      }
      this.ws.onclose = () => {
        this.state = 'disconnected'
      }
    })
  }

  send(event: GameEvent): void {
    if (this.state !== 'matched' || this.ws === null || this.matchId === null) return
    this.ws.send(JSON.stringify({ type: 'GAME_EVENT', matchId: this.matchId, event }))
  }

  onReceive(cb: (e: GameEvent) => void): void {
    this.receiveCallback = cb
  }

  disconnect(): void {
    this.ws?.close()
    this.ws = null
    this.state = 'disconnected'
  }

  getState(): ConnectionState {
    return this.state
  }

  /** Returns the server-assigned player role ('player1'|'player2'), or null before match is found. */
  getLocalPlayerId(): 'player1' | 'player2' | null {
    return this._localPlayerId
  }

  private handleMessage(raw: string): void {
    let msg: Record<string, unknown>
    try {
      msg = JSON.parse(raw) as Record<string, unknown>
    } catch {
      return
    }
    if (msg['type'] === 'QUEUED') {
      this.opts.onQueued?.(msg['position'] as number)
    }
    if (msg['type'] === 'MATCH_FOUND') {
      this.matchId = msg['matchId'] as string
      this.state = 'matched'
      // Prefer explicit localPlayerId field (new server); fall back to youAre (compat)
      const assignedId = (msg['localPlayerId'] ?? msg['youAre']) as 'player1' | 'player2'
      this._localPlayerId = assignedId
      this.opts.onMatchFound?.(
        msg['matchId'] as string,
        msg['opponentId'] as string,
        msg['opponentName'] as string,
        assignedId,
        msg['opponentMmr'] as number | undefined
      )
    }
    if (msg['type'] === 'GAME_EVENT') {
      this.receiveCallback?.(msg['event'] as GameEvent)
    }
    if (msg['type'] === 'OPPONENT_DISCONNECTED') {
      this.opts.onOpponentDisconnected?.()
      // Also dispatch a DOM event so MatchScene can react regardless of how the
      // transport was constructed (e.g. when handed off via window.__wsTransport).
      window.dispatchEvent(new CustomEvent('ultimaze:opponent-disconnected', {
        detail: { matchId: msg['matchId'] },
      }))
    }
    // ── Coop-specific relay messages ──────────────────────────────────────────
    if (msg['type'] === 'COOP_PARTNER_HP') {
      const hp = msg['hp'] as number
      this.opts.onCoopPartnerHp?.(hp)
      // Also dispatch a DOM event so MatchScene / HUD can react without tight coupling.
      window.dispatchEvent(new CustomEvent('ultimaze:coop-partner-hp', { detail: { hp } }))
    }
    if (msg['type'] === 'COOP_PARTNER_DEFEATED') {
      this.opts.onCoopPartnerDefeated?.()
      window.dispatchEvent(new CustomEvent('ultimaze:coop-partner-defeated'))
    }
    // ── Server-driven coop wave events ───────────────────────────────────────
    if (msg['type'] === 'COOP_WAVE_START') {
      window.dispatchEvent(new CustomEvent('ultimaze:coop-wave-start', {
        detail: { wave: msg['wave'] as number, total: msg['total'] as number },
      }))
    }
    if (msg['type'] === 'COOP_VICTORY') {
      window.dispatchEvent(new CustomEvent('ultimaze:coop-victory', {
        detail: { message: msg['message'] as string },
      }))
    }
    // ── Server-relayed wave-bot UNIT_SENT events are passed through GAME_EVENT
    // (msg.type === 'GAME_EVENT') and forwarded to the engine via receiveCallback above.
    if (msg['type'] === 'CHAT_MESSAGE') {
      window.dispatchEvent(new CustomEvent('ultimaze:chat', { detail: msg }))
    }
  }

  /** Send a coop partner HP update to the server for relay. */
  sendCoopHp(hp: number): void {
    if (this.ws === null || this.ws.readyState !== WebSocket.OPEN) return
    this.ws.send(JSON.stringify({ type: 'COOP_PARTNER_HP', hp }))
  }

  /** Notify the server (and the partner) that this player's base was destroyed. */
  sendCoopDefeat(): void {
    if (this.ws === null || this.ws.readyState !== WebSocket.OPEN) return
    this.ws.send(JSON.stringify({ type: 'COOP_DEFEAT' }))
  }

  /** Send a chat message to the server for relay to opponent(s) and spectators. */
  sendChat(text: string): void {
    if (this.ws === null || this.ws.readyState !== WebSocket.OPEN) return
    this.ws.send(JSON.stringify({ type: 'CHAT_MESSAGE', text }))
  }
}
